import { LatLng } from 'leaflet';
import { ReactElement, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import { useAtlasTracking, usePrevious, useQuery } from '../../../hooks';

import { MapDetailDto } from '../../../models';
import {
	Project,
	AppState,
	AppThunkDispatch,
	ProjectAction,
	projectFetch,
	fetchMap,
	projectLoadFromMap,
	projectClearState,
	httpGet,
	HttpAction
} from '../../../redux';
import { LoadingContainer } from '../../atoms';
import { PageContainer, CreateComparison } from '../../molecules';
import { ProjectViewer } from '../../organisms';

import './atlas-viewer.styles.scss';

export interface IAtlasViewerProps {
	loadedMaps: { [mapId: string]: MapDetailDto };
	project?: Project;
	fetchProject: (projectId: string) => void;
	fetchMap: (metadataId: string) => void;
	loadMapAsProject: (map: MapDetailDto) => void;
	clearProjectState: () => void;
	startLoading: (section: string) => void;
}

interface IRoutingParams {
	projectId?: string;
	mapId?: string;
	comparisonMapId?: string;
}

function AtlasViewerView(props: IAtlasViewerProps): ReactElement {
	const {
		loadedMaps,
		project,
		fetchMap,
		fetchProject,
		loadMapAsProject,
		clearProjectState,
		startLoading
	} = props;
	const { projectId, mapId, comparisonMapId } = useParams<IRoutingParams>();
	const previousComparisonMapId: string | undefined =
		usePrevious<string | undefined>(comparisonMapId);

	const map: MapDetailDto | undefined = useMemo(() => {
		return loadedMaps[getCurrentMapId()];
	}, [loadedMaps, mapId, project, comparisonMapId]);
	const queryParams = useQuery();
	const topographicLocation: LatLng | undefined = useMemo<
		LatLng | undefined
	>(() => {
		const latString: string | null = queryParams.get('lat');
		const lngString: string | null = queryParams.get('lng');

		if (!latString || !lngString) return;

		const lat: number = Number(latString);
		const lng: number = Number(lngString);

		return new LatLng(lat, lng);
	}, [queryParams]);
	const topographicName: string | null = queryParams.get('name');

	useAtlasTracking(mapId, projectId);

	useEffect(() => {
		if (mapId !== undefined && project !== undefined) {
			clearProjectState();
			if (map !== undefined) {
				loadMapAsProject(map);
			}
		}
	}, [mapId]);

	useEffect(() => {
		if (projectId) {
			startLoading('project');
			fetchProject(projectId);
		}
	}, [projectId]);

	useEffect(() => {
		if (shouldLoadMap()) {
			startLoading('map');
			fetchMap(getCurrentMapId());
		}
	}, [mapId, project]);

	useEffect(() => {
		if (shouldLoadProject()) {
			loadMapAsProject(map);
		}
	}, [map]);

	useEffect(() => {
		if (shouldReloadProject()) {
			startLoading('project');
			fetchProject(project!.id!);
		}
	}, [comparisonMapId]);

	function shouldLoadMap(): boolean {
		return (
			(mapId !== undefined && map === undefined) ||
			(project !== undefined && map === undefined) ||
			(project !== undefined &&
				map !== undefined &&
				project.metaDataId !== map.id) ||
			(comparisonMapId !== undefined && map === undefined)
		);
	}

	function shouldReloadProject(): boolean {
		return (
			!previousComparisonMapId &&
			comparisonMapId !== undefined &&
			project !== undefined &&
			project.id !== undefined
		);
	}

	function shouldLoadProject(): boolean {
		return (
			(map !== undefined && project === undefined) ||
			(project !== undefined &&
				map !== undefined &&
				project.metaDataId !== map.id)
		);
	}

	function getCurrentMapId(): string {
		let metadataId: string = '';
		if (mapId) metadataId = mapId;
		if (project && !mapId) metadataId = project.metaDataId;
		if (comparisonMapId) metadataId = comparisonMapId;

		return metadataId;
	}

	return (
		<PageContainer id="atlas-viewer" noPadding>
			<LoadingContainer
				sections={['map', 'project']}
				excludedCalls={[
					{ url: 'projects', method: 'HTTP_POST' },
					{ url: 'projects/:id', method: 'HTTP_GET' }
				]}
			>
				{!comparisonMapId && project && map && (
					<ProjectViewer
						map={map}
						project={project}
						topographicLocation={topographicLocation}
						topographicName={
							topographicName ? topographicName : undefined
						}
					/>
				)}
			</LoadingContainer>
			{comparisonMapId && project && (
				<CreateComparison project={project} />
			)}
		</PageContainer>
	);
}

function mapStateToProps(state: AppState) {
	return {
		project: state.project.present.project,
		loadedMaps: state.maps.loadedMaps
	};
}

function mapDispatchToProps(
	dispatch: AppThunkDispatch<ProjectAction | HttpAction>
) {
	return {
		fetchProject: (projectId: string) => dispatch(projectFetch(projectId)),
		fetchMap: (metadataId: string) => dispatch(fetchMap(metadataId)),
		loadMapAsProject: (map: MapDetailDto) =>
			dispatch(projectLoadFromMap(map)),
		clearProjectState: () => dispatch(projectClearState()),
		startLoading: (section: string) => dispatch(httpGet(section))
	};
}

export const AtlasViewer = connect(
	mapStateToProps,
	mapDispatchToProps
)(AtlasViewerView);
